import { LogOut, Menu, Shield, User } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, matchPath, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";

import { ApiKeyDialog } from "@/components/api-key-dialog";
import { ApiKeyRow } from "@/components/api-key-row";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAdminKey, useApiKey } from "@/hooks/use-auth";
import { useAsync } from "@/hooks/use-async";
import { studentApi } from "@/lib/api";
import { adminKeyStore, apiKeyStore } from "@/lib/auth";
import { cn } from "@/lib/utils";

type NavEntry = { to: string; label: string; end: boolean };

// The tutorial is the front page, reached by the wordmark; the nav is for the two places a
// student comes back to.
const NAV: NavEntry[] = [
  { to: "/challenges", label: "Challenges", end: false },
  { to: "/team", label: "My team", end: false },
];

const ADMIN_NAV: NavEntry = { to: "/admin", label: "Admin", end: false };

function NavItem({ to, label, end }: NavEntry) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
          isActive
            ? "bg-accent text-accent-foreground"
            : "text-muted-foreground hover:text-foreground",
        )
      }
    >
      {label}
    </NavLink>
  );
}

/** Hamburger menu holding the primary nav on narrow screens. */
function MobileNav({ items }: { items: NavEntry[] }) {
  const { pathname } = useLocation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="ghost" size="icon" aria-label="Open navigation">
            <Menu aria-hidden />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuGroup>
          {items.map((item) => {
            const active =
              matchPath({ path: item.to, end: item.end }, pathname) !== null;
            return (
              <DropdownMenuItem
                key={item.to}
                render={<Link to={item.to} />}
                className={cn(
                  "py-2",
                  active && "bg-accent text-accent-foreground font-medium",
                )}
              >
                {item.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SiteHeader() {
  const apiKey = useApiKey();
  const adminKey = useAdminKey();
  const navigate = useNavigate();
  const [signInOpen, setSignInOpen] = useState(false);

  const me = useAsync(
    apiKey ? () => studentApi.me(apiKey) : null,
    [apiKey],
  );

  const navItems = adminKey ? [...NAV, ADMIN_NAV] : NAV;

  function signOut() {
    apiKeyStore.clear();
    adminKeyStore.clear();
    toast.success("Signed out");
    navigate("/");
  }

  return (
    <header className="bg-background/80 sticky top-0 z-40 w-full border-b backdrop-blur">
      <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 sm:h-16 sm:gap-4">
        <NavLink
          to="/"
          className="flex min-w-0 items-center gap-2.5 font-semibold sm:gap-3"
        >
          {/* The logo is NYU violet on transparent. In dark mode the filter
              chain flattens it to a single light-violet tone (~#c9b1e7) so it
              sits on the dark bg without a white tile behind it. */}
          <img
            src={`${import.meta.env.BASE_URL}ai4ce-block.webp`}
            alt="AI4CE Lab"
            width={40}
            height={40}
            className="size-9 shrink-0 sm:size-10 dark:[filter:brightness(0)_invert(0.6)_sepia(1)_hue-rotate(225deg)]"
          />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-sm sm:text-[15px]">
              Visual Navigation Challenge
            </span>
            <span className="text-muted-foreground truncate text-[10px] font-medium tracking-wide uppercase sm:text-[11px]">
              AI4CE Lab · NYU
            </span>
          </span>
        </NavLink>

        <nav
          className="ml-2 hidden items-center gap-1 sm:flex"
          aria-label="Main"
        >
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
          {apiKey ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 px-2 sm:px-2.5"
                    aria-label="Account"
                  >
                    <User className="size-4" aria-hidden />
                    <span className="hidden max-w-[14ch] truncate sm:inline">
                      {me.data?.email ?? "Account"}
                    </span>
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="w-64">
                {/* Base UI requires GroupLabel (DropdownMenuLabel) to have a
                    Group ancestor, and wrapping the items too means the group is
                    correctly labelled for assistive technology. */}
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="font-normal">
                    <p className="truncate text-sm font-medium">
                      {me.data?.email ?? "Signed in"}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {me.data
                        ? (me.data.team?.name ?? "No team yet")
                        : "Loading…"}
                    </p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* The key the browser signed in with, for the terminal or a second
                      machine. A label block rather than an item: pressing its buttons
                      must not close the menu. */}
                  <DropdownMenuLabel className="font-normal">
                    <p className="text-muted-foreground mb-1 text-xs">API key</p>
                    <ApiKeyRow apiKey={apiKey} />
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {adminKey ? (
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <Shield className="size-4" aria-hidden />
                      Admin console
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem onClick={signOut} variant="destructive">
                    <LogOut className="size-4" aria-hidden />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button size="sm" onClick={() => setSignInOpen(true)}>
                Sign in
              </Button>
              <ApiKeyDialog open={signInOpen} onOpenChange={setSignInOpen} />
            </>
          )}

          <div className="sm:hidden">
            <MobileNav items={navItems} />
          </div>
        </div>
      </div>
    </header>
  );
}
